import { useCreate, useNotify, useTranslate, useUpdate } from "ra-core";
import { useEffect, useId, useState, type FormEvent } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useIsMobile } from "@/hooks/use-mobile";

import {
  competitorFormDefaults,
  normalizeCompetitorForm,
  type CompetitorFormValues,
} from "./competitors";
import {
  DateField,
  Field,
  FieldError,
  FormDialog,
  SELECT_CLASS,
} from "@/components/market-intel/FormDialog";
import {
  COMPETITOR_LINES,
  lineLabelKey,
  TRACKING_STATUSES,
  trackingStatusLabelKey,
} from "@/components/market-intel/labels";
import type { MarketCompetitor } from "@/components/market-intel/types";

interface CompetitorFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Edits this competitor; creates a new one when absent. */
  competitor?: MarketCompetitor | null;
  onCreated?: (competitor: MarketCompetitor) => void;
}

/** Postgres unique violation (name), as surfaced by PostgREST / FakeRest. */
const isDuplicate = (error: unknown): boolean =>
  /23505|duplicate|unique/i.test(
    error instanceof Error ? error.message : String(error ?? ""),
  );

type TextKey = Exclude<keyof CompetitorFormValues, "line" | "tracking_status">;

export const CompetitorForm = ({
  open,
  onOpenChange,
  competitor,
  onCreated,
}: CompetitorFormProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const isMobile = useIsMobile();
  const formId = useId();
  const [values, setValues] = useState(() =>
    competitorFormDefaults(competitor ?? undefined),
  );
  const [error, setError] = useState<string | null>(null);
  const [create, { isPending: creating }] = useCreate<MarketCompetitor>();
  const [update, { isPending: updating }] = useUpdate<MarketCompetitor>();

  useEffect(() => {
    if (open) {
      setValues(competitorFormDefaults(competitor ?? undefined));
      setError(null);
    }
  }, [open, competitor]);

  const set =
    <K extends keyof CompetitorFormValues>(key: K) =>
    (value: CompetitorFormValues[K]) =>
      setValues((current) => ({ ...current, [key]: value }));
  const close = () => onOpenChange(false);
  const onError = (failure: unknown) =>
    notify(
      isDuplicate(failure)
        ? "market.competitors.duplicate"
        : "market.competitors.error",
      { type: "error" },
    );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const result = normalizeCompetitorForm(values);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    if (competitor) {
      update(
        "market_competitors",
        { id: competitor.id, data: result.data, previousData: competitor },
        {
          mutationMode: "optimistic",
          onSuccess: () =>
            notify("market.competitors.updated", { type: "success" }),
          onError,
        },
      );
      close();
      return;
    }
    create(
      "market_competitors",
      { data: result.data },
      {
        onSuccess: (created) => {
          notify("market.competitors.created", { type: "success" });
          close();
          onCreated?.(created);
        },
        onError,
      },
    );
  };

  const errorId = `${formId}-error`;
  const text = (
    key: TextKey,
    props: { type?: string; inputMode?: "url" } = {},
  ) => (
    <Field
      id={`${formId}-${key}`}
      label={translate(`market.competitors.form.${key}`)}
    >
      <Input
        id={`${formId}-${key}`}
        value={values[key]}
        onChange={(event) => set(key)(event.target.value)}
        {...props}
      />
    </Field>
  );
  const area = (key: TextKey, rows = 3) => (
    <Field
      id={`${formId}-${key}`}
      label={translate(`market.competitors.form.${key}`)}
    >
      <Textarea
        id={`${formId}-${key}`}
        value={values[key]}
        onChange={(event) => set(key)(event.target.value)}
        rows={rows}
        aria-describedby={`${formId}-markdown-hint`}
      />
    </Field>
  );
  const title = translate(
    competitor ? "market.competitors.edit_title" : "market.competitors.add",
  );

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      formId={formId}
      onSubmit={submit}
      className="sm:max-w-2xl"
      actions={
        <>
          <Button
            type="button"
            variant="outline"
            className="rounded-full max-md:hidden"
            onClick={close}
          >
            {translate("market.competitors.form.cancel")}
          </Button>
          <Button
            type="submit"
            form={formId}
            disabled={creating || updating}
            className="btn-save rounded-full px-5 max-md:h-12 max-md:flex-1"
          >
            {translate("market.competitors.form.save")}
          </Button>
        </>
      }
    >
      <Field
        id={`${formId}-name`}
        label={translate("market.competitors.form.name")}
      >
        <Input
          id={`${formId}-name`}
          value={values.name}
          onChange={(event) => {
            set("name")(event.target.value);
            setError(null);
          }}
          aria-invalid={
            error === "market.competitors.form.name_required" ||
            error === "market.competitors.form.name_too_long" ||
            undefined
          }
          aria-describedby={error ? errorId : undefined}
          autoFocus={!isMobile}
          maxLength={160}
        />
      </Field>
      {error ? <FieldError id={errorId}>{translate(error)}</FieldError> : null}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          id={`${formId}-line`}
          label={translate("market.competitors.form.line")}
        >
          <select
            id={`${formId}-line`}
            className={SELECT_CLASS}
            value={values.line}
            onChange={(event) =>
              set("line")(event.target.value as CompetitorFormValues["line"])
            }
          >
            {COMPETITOR_LINES.map((line) => (
              <option key={line} value={line}>
                {translate(lineLabelKey(line))}
              </option>
            ))}
          </select>
        </Field>
        <Field
          id={`${formId}-status`}
          label={translate("market.tracking_status.label")}
        >
          <select
            id={`${formId}-status`}
            className={SELECT_CLASS}
            value={values.tracking_status}
            onChange={(event) =>
              set("tracking_status")(
                event.target.value as CompetitorFormValues["tracking_status"],
              )
            }
          >
            {TRACKING_STATUSES.map((status) => (
              <option key={status} value={status}>
                {translate(trackingStatusLabelKey(status))}
              </option>
            ))}
          </select>
        </Field>
        {text("website", { type: "url", inputMode: "url" })}
        {text("logo_url", { type: "url", inputMode: "url" })}
        {text("location")}
        {text("entry_price")}
        <Field
          id={`${formId}-last_reviewed_at`}
          label={translate("market.competitors.form.last_reviewed_at")}
        >
          <DateField
            id={`${formId}-last_reviewed_at`}
            value={values.last_reviewed_at}
            onChange={(event) => {
              set("last_reviewed_at")(event.target.value);
              setError(null);
            }}
          />
        </Field>
      </div>
      {area("value_proposition", 2)}
      {text("channels")}
      {text("pricing")}
      {area("description")}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {area("strengths")}
        {area("weaknesses")}
      </div>
      {area("conclusion", 2)}
      {area("notes")}
      <p
        id={`${formId}-markdown-hint`}
        className="text-xs text-muted-foreground"
      >
        {translate("market.competitors.form.markdown_hint")}
      </p>
    </FormDialog>
  );
};
